import { useMemo } from 'react'

import { sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import matchFilterNumber from '@functions/utils/matchFilterNumber'
import matchFilterString from '@functions/utils/matchFilterString'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { AdAccountGroupLinkModel, AdAccountGroupLinkRequestOptions, ClientModel, UserModel } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const getLinkableEntityName = (
  adAccountGroupLink: AdAccountGroupLinkModel,
  clients: { [key: number]: ClientModel },
  users: { [key: number]: UserModel }
) => {
  if (adAccountGroupLink.linkableType === 'Client') {
    const client = clients[adAccountGroupLink.linkableId] || {}
    return client.name
  }

  const user = users[adAccountGroupLink.linkableId] || {}
  return user.name
}

const watchEntityKeys = ['adAccountGroupLinks']

type AdAccountGroupLinkFilters = {
  adAccountGroupId?: number,
  linkableEntityName?: string,
  linkableType?: string,
}

type UseAdAccountGroupLinksOptions = {
  filters?: AdAccountGroupLinkFilters,
  performHttpRequests?: boolean,
  requestOptions?: AdAccountGroupLinkRequestOptions,
}

function useAdAccountGroupLinks(options: UseAdAccountGroupLinksOptions) {
  const { filters = {}, requestOptions } = options
  const {
    adAccountGroupId: filterAdAccountGroupId,
    linkableEntityName: filterLinkableEntityName,
    linkableType: filterLinkableType,
  } = filters

  const {
    updatedEntities: { adAccountGroupLinks: adAccountGroupLinksUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { adAccountGroupLinks, clients, users } = useSelector(reduxState => reduxState.entities)

  const filteredAdAccountGroupLinks = useMemo(() => {
    const filtered = Object.values(adAccountGroupLinks).filter(adAccountGroupLink => {
      const { adAccountGroupId, linkableType } = adAccountGroupLink

      const matchAdAccountGroupId = matchFilterNumber(adAccountGroupId, filterAdAccountGroupId)

      const linkableEntityName = getLinkableEntityName(adAccountGroupLink, clients, users)
      const matchLinkableEntityName = matchFilterString(linkableEntityName, filterLinkableEntityName)

      const matchLinkableType = filterLinkableType ? linkableType === filterLinkableType : true

      return matchAdAccountGroupId && matchLinkableEntityName && matchLinkableType
    })

    return sortArrayBy(
      filtered,
      'asc',
      ({ linkableId }) => (
        filterLinkableType === 'Client'
          ? clients[linkableId]?.name
          : users[linkableId]?.name
      )
    )
  }, [adAccountGroupLinksUpdatedAt, JSON.stringify(filters)])

  const filteredAdAccountGroupLinksCount = filteredAdAccountGroupLinks.length
  const hasFilteredAdAccountGroupLinks = !!filteredAdAccountGroupLinksCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredAdAccountGroupLinksCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingAdAccountGroupLinks } = useReduxAction(
    'adAccountGroupLinks',
    'loadAdAccountGroupLinks',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAdAccountGroupLinks,
    filteredAdAccountGroupLinksCount,
    hasFilteredAdAccountGroupLinks,
    loading: loadingAdAccountGroupLinks,
  }
}

export default useAdAccountGroupLinks
